import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Button, cn } from '@forecastify/ui';
import {
  CONTROL_PANEL_PATHS,
  OPERATOR_SPEND_WINDOWS,
  operatorAgentSpendResponseSchema,
  type OperatorAgentSpendResponse,
} from '@forecastify/shared';
import { useI18n, useT, type MessageKey } from '../i18n';
import { useOperatorSession } from '../session';
import { formatDateTime, formatNumber } from '../chrome/format';
import { BTN_SM, TH, TH_ROW } from '../chrome/tones';
import { SkeletonRows } from '../chrome/skeleton-rows';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ *Agent-használat* — who is asking the agent how much, and who hit the cap.│
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE CEILING COLUMN IS ALWAYS TODAY'S, WHATEVER WINDOW IS CHOSEN.** The cap resets at the
 * UTC day boundary, so *"Mai plafon"* over a thirty-day window would be meaningless if it divided
 * thirty days of tokens by one day's allowance. So the screen reads two windows: the chosen one for
 * the figures, and the one-day window for the bar. The redesign's column heading says *Mai*; this
 * is what makes that true.
 *
 * ⚠️ **"Billed tokens" is input plus output.** Cache reads are cheaper and the redesign's figure is
 * the one the cap counts, which is what `0121`'s ceiling compares against.
 */

type Load =
  | { readonly state: 'loading' }
  | { readonly state: 'failed' }
  | {
      readonly state: 'ready';
      readonly window: OperatorAgentSpendResponse;
      readonly today: OperatorAgentSpendResponse;
    };

const WINDOW_LABEL: Readonly<Record<(typeof OPERATOR_SPEND_WINDOWS)[number], MessageKey>> = {
  1: 'spend.window.today',
  7: 'spend.window.7',
  30: 'spend.window.30',
  90: 'spend.window.90',
};

export function AgentSpendScreen(): ReactNode {
  const t = useT();
  const { locale } = useI18n();
  const session = useOperatorSession();
  const [days, setDays] = useState<(typeof OPERATOR_SPEND_WINDOWS)[number]>(1);
  const [load, setLoad] = useState<Load>({ state: 'loading' });

  const read = useCallback(async (): Promise<void> => {
    const [chosen, today] = await Promise.all([
      session.request<unknown>(CONTROL_PANEL_PATHS.agentSpend(days)),
      session.request<unknown>(CONTROL_PANEL_PATHS.agentSpend(1)),
    ]);
    if (!chosen.ok || !today.ok) {
      if ((!chosen.ok && chosen.status === 401) || (!today.ok && today.status === 401)) return;
      setLoad({ state: 'failed' });
      return;
    }
    const a = operatorAgentSpendResponseSchema.safeParse(chosen.value);
    const b = operatorAgentSpendResponseSchema.safeParse(today.value);
    setLoad(
      a.success && b.success
        ? { state: 'ready', window: a.data, today: b.data }
        : { state: 'failed' },
    );
  }, [session, days]);

  useEffect(() => {
    void read();
  }, [read]);

  const cap = load.state === 'ready' ? load.window.cap : null;

  return (
    <div data-screen="agent-spend" className="flex flex-col gap-3 p-5">
      <div className="flex flex-wrap items-center gap-3">
        <div
          role="group"
          aria-label={t('spend.window')}
          className="bg-muted flex gap-0.5 rounded-md p-0.5"
        >
          {OPERATOR_SPEND_WINDOWS.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={option === days}
              onClick={() => {
                setDays(option);
              }}
              className={cn(
                'h-7 rounded px-2.5 text-[12.5px] font-medium whitespace-nowrap',
                option === days
                  ? 'bg-background text-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t(WINDOW_LABEL[option])}
            </button>
          ))}
        </div>
        <span className="text-muted-foreground min-w-0 flex-1 text-[12px] text-pretty">
          {load.state === 'loading'
            ? null
            : cap === null || cap === 0
              ? t('spend.noCap')
              : t('spend.note', { cap: formatNumber(cap, locale) })}
        </span>
      </div>

      {load.state === 'failed' ? (
        <div role="alert" className="flex flex-col items-start gap-2">
          <p className="text-[13px]">{t('spend.failed')}</p>
          <Button
            variant="outline"
            size="sm"
            className={BTN_SM}
            onClick={() => {
              void read();
            }}
          >
            {t('tenants.retry')}
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table aria-label={t('spend.heading')} className="w-full min-w-[860px] text-[13px]">
            <thead className="bg-muted/40 border-b">
              <tr className={TH_ROW}>
                <th className={TH}>{t('spend.column.tenant')}</th>
                <th className={cn(TH, 'text-right')}>{t('spend.column.calls')}</th>
                <th className={cn(TH, 'text-right')}>{t('spend.column.questions')}</th>
                <th className={cn(TH, 'text-right')}>{t('spend.column.tokens')}</th>
                <th className={TH}>{t('spend.column.last')}</th>
                <th className={cn(TH, 'w-[180px]')}>{t('spend.column.ceiling')}</th>
              </tr>
            </thead>
            <tbody>
              {load.state === 'loading' ? (
                <SkeletonRows columns={6} rowClassName="h-11" rightAligned={[1, 2, 3]} />
              ) : (
                load.window.rows.map((row) => {
                  const todayRow = load.today.rows.find((r) => r.workspaceId === row.workspaceId);
                  const used =
                    todayRow === undefined ? 0 : todayRow.inputTokens + todayRow.outputTokens;
                  const pct = cap === null || cap === 0 ? null : Math.min(100, (used / cap) * 100);
                  const atCeiling = pct !== null && pct >= 100;
                  return (
                    <tr key={row.workspaceId} className="h-11 border-b last:border-0">
                      <td className="max-w-[260px] px-3">
                        <span className="block truncate font-medium">{row.tenantName}</span>
                        {/* ⚠️ Where the turns ran (R-27, `0043`) — no column in the drawing, so it
                          trails the workspace name rather than being dropped. */}
                        <span className="text-muted-foreground block truncate font-mono text-[11px]">
                          <span>{row.workspaceName}</span>
                          {row.inferenceGeos.length > 0 ? (
                            <span>{` · ${row.inferenceGeos.join(', ')}`}</span>
                          ) : null}
                        </span>
                      </td>
                      <td className="px-3 text-right tabular-nums">
                        {formatNumber(row.calls, locale)}
                      </td>
                      <td className="px-3 text-right tabular-nums">
                        {formatNumber(row.questions, locale)}
                      </td>
                      <td className="px-3 text-right tabular-nums">
                        {formatNumber(row.inputTokens + row.outputTokens, locale)}
                      </td>
                      <td className="px-3 tabular-nums">
                        {formatDateTime(row.lastTurnAt, locale, {
                          today: t('when.today'),
                          yesterday: t('when.yesterday'),
                        })}
                      </td>
                      <td className="px-3">
                        {pct === null ? (
                          <span className="text-muted-foreground text-[11.5px]">–</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="bg-muted h-1.5 min-w-0 flex-1 overflow-hidden rounded-full">
                              <div
                                className={cn(
                                  'h-full rounded-full',
                                  atCeiling ? 'bg-[var(--status-warn-line)]' : 'bg-primary/70',
                                )}
                                style={{ width: `${String(pct)}%` }}
                              />
                            </div>
                            <span
                              className={cn(
                                'w-[74px] shrink-0 text-right text-[11.5px] whitespace-nowrap tabular-nums',
                                atCeiling
                                  ? 'font-medium text-[var(--status-warn-text)]'
                                  : 'text-muted-foreground',
                              )}
                            >
                              {atCeiling ? t('spend.atCeiling') : `${String(Math.round(pct))}%`}
                            </span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          {load.state === 'loading' ? (
            <p role="status" className="sr-only">
              {t('tenants.loading')}
            </p>
          ) : null}
          {load.state === 'ready' && load.window.rows.length === 0 ? (
            <div className="text-muted-foreground px-6 py-10 text-center text-[12.5px]">
              {t('spend.empty')}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
